import React from 'react';
import { Card, CardActionArea, CardContent, CardMedia } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { GlobalAppCollection } from 'models/app';
import { truncateString } from '../../utils/helpers';
import { StarRating } from '../StarRating';
import styles from './styles.module.scss';
import defaultImg from '../../assets/icon_not_loading.png';
import { detectLocale } from '../../utils/transformationHelper';
import { Link as RouteLink } from 'react-router-dom';
import { generateRoute } from '../../utils/location';
import { canShowStarRating } from '../../component-helpers/app-reviews';
import { useRatingsAndReviews } from '../../contexts/providers/ratings-reviews';
import { ChevronRight } from '@material-ui/icons';
import { AppMarketPageIdentifier } from '../../AppRoutes';
import ctx from 'classnames';

export const SnackTile: React.FC<{ collection: GlobalAppCollection }> = ({ collection }) => {
  const context = useRatingsAndReviews();
  const { i18n, t } = useTranslation(),
    tagLineLength = 45,
    usLocale = i18n.language === 'en-US';
  const appList = collection.carouselApps.length > 1;
  const collectionDisplayName = usLocale
    ? collection.displayName
    : t('collectionName.' + collection.displayName + '.display_name', collection.displayName);
  const translatedExploreMore = t('app_collections.explore_more');

  return (
    <Card elevation={1} square className={styles['snack-card']}>
      <p className={ctx("secondaryText", styles.title)} data-testid='collectionName'>
        {collectionDisplayName}
      </p>
      {collection.carouselApps.slice(0, 2).map(app => {
        const {id, developer, aggregateRating, filenameIconSmall, name, description} = app;
        const canShowRatings = canShowStarRating(id, developer.id, aggregateRating.reviewCount, context);
        return (
          <CardActionArea
            className={styles.card}
            disableRipple
            component={RouteLink}
            to={generateRoute(AppMarketPageIdentifier.AppDetails, id)} key={id}
            style={{ textDecoration: 'none' }}
            role='link'
			      data-testid='routeLink'
          >
            <CardContent className={styles['card-content']}>
              <div>
                <CardMedia
                  src={filenameIconSmall || defaultImg}
                  component='img'
                  className={styles.icon}
                  onError={(e: any) => {
                    e.target.src = defaultImg;
                  }}
                  role='none'
                />
                <div className={styles.body}>
                  <p>
                    <strong>
                    {name || detectLocale(app)?.name}
                    </strong>
                  </p>
                  {description && (
                  <div className={styles.description}>
                      <p className="captionText">
                    <small>
                      {
                        truncateString(detectLocale(app)?.tagline?.trim() || '', tagLineLength) ||
                        truncateString(app?.tagline?.trim() || '', tagLineLength) ||
                        truncateString(detectLocale(app)?.description?.trim() || '', tagLineLength) ||
                        truncateString(description?.trim() || '', tagLineLength)
                      }
                    </small>
                  </p>
                  </div>
                  )}
                  {canShowRatings ? <StarRating rating={aggregateRating} size={'small'} /> : ''}
                </div>
              </div>
            </CardContent>
          </CardActionArea>
        );
      })}
      <div className={styles.carouselLinkWrapper}>
        {appList && (
          <RouteLink to={generateRoute(AppMarketPageIdentifier.Collections, collection.id)} tabIndex={0}>
            <div
              role="button"
              color="inherit"
              className={styles.carouselLink}
              aria-label={translatedExploreMore + collectionDisplayName}
			  data-testid='exploreMore'
            >
              {translatedExploreMore}<ChevronRight className={styles.chevronRight}/>
            </div>
          </RouteLink>
        )}
      </div>
    </Card>
  );
};
